import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Users, Crown, Building2, Loader2, AlertCircle } from 'lucide-react';
import logo from '../assets/logo.svg';
import { getPublicPlans, PublicTier, PublicPlansResponse } from '../api/subscriptions';

// ChargeKeep payment configuration - includes URL and invoice ID for proper popup
interface ChargeKeepPaymentConfig {
  url: string;
  invoiceId: string;
}

const CHARGEKEEP_PAYMENT_LINKS: Record<string, Record<'monthly' | 'yearly', ChargeKeepPaymentConfig | null>> = {
  basic: {
    monthly: { url: 'https://app.chargekeep.com/i/f81e8b38d55b6930', invoiceId: '60576' },
    yearly: { url: 'https://app.chargekeep.com/i/5f2045da4da96fae', invoiceId: '60577' },
  },
  advanced: {
    monthly: { url: 'https://app.chargekeep.com/i/9146815c3f7fa019', invoiceId: '60580' },
    yearly: { url: 'https://app.chargekeep.com/i/1b2174dc1e8066ae', invoiceId: '60578' },
  },
  pro: {
    monthly: { url: 'https://app.chargekeep.com/i/a39d3a3aa058f7ea', invoiceId: '60581' },
    yearly: { url: 'https://app.chargekeep.com/i/f8c4a952865547a9', invoiceId: '60579' },
  },
  org_5: {
    monthly: { url: 'https://app.chargekeep.com/i/f1849b4911244ac2', invoiceId: '60582' },
    yearly: { url: 'https://app.chargekeep.com/i/1583ff189d88b64c', invoiceId: '60585' },
  },
  org_10: {
    monthly: { url: 'https://app.chargekeep.com/i/acad6708f2ada48f', invoiceId: '60583' },
    yearly: { url: 'https://app.chargekeep.com/i/b74bbef58111837b', invoiceId: '60586' },
  },
  org_25: {
    monthly: { url: 'https://app.chargekeep.com/i/f143e31d070cd120', invoiceId: '60584' },
    yearly: { url: 'https://app.chargekeep.com/i/d02191ada6c21ac7', invoiceId: '60587' },
  },
};

// Get ChargeKeep config for a tier
const getChargeKeepConfig = (tierCode: string, billingPeriod: 'monthly' | 'yearly'): ChargeKeepPaymentConfig | null => {
  return CHARGEKEEP_PAYMENT_LINKS[tierCode]?.[billingPeriod] || null;
};

// THE WORKING HELPER FUNCTION - creates anchor, loads script, waits, then clicks
const openChargeKeepPopup = (config: ChargeKeepPaymentConfig): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 1. Create hidden anchor element with the ChargeKeep class
    const anchor = document.createElement('a');
    anchor.href = config.url;
    anchor.className = 'chargekeep-payment-form-btn';
    anchor.id = `chargekeep-payment-${config.invoiceId}`;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);

    // 2. Load the embed script for this invoice
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://app.chargekeep.com/invoices/${config.invoiceId}/embed_form_script.js`;

    // 3. WAIT for script to load, THEN click anchor to trigger popup
    script.onload = () => {
      setTimeout(() => {
        anchor.click(); // THIS triggers the ChargeKeep popup
        resolve();
      }, 100);
    };

    script.onerror = () => {
      document.body.removeChild(anchor);
      reject(new Error('Failed to load ChargeKeep payment form'));
    };

    document.body.appendChild(script);

    // 4. Cleanup after delay (popup stays open)
    setTimeout(() => {
      if (anchor.parentNode) document.body.removeChild(anchor);
      if (script.parentNode) document.body.removeChild(script);
    }, 2000);
  });
};

// Get icon for tier
const getTierIcon = (tierCode: string, isPopular: boolean) => {
  const iconClass = `w-6 h-6 ${isPopular ? 'text-accent-orange' : 'text-accent-blue'}`;

  if (tierCode.startsWith('org_')) {
    const users = parseInt(tierCode.split('_')[1]);
    return users >= 10 ? <Building2 className={iconClass} /> : <Users className={iconClass} />;
  }

  switch (tierCode) {
    case 'basic':
      return <Sparkles className={iconClass} />;
    case 'advanced':
    case 'pro':
      return <Crown className={iconClass} />;
    default:
      return <Sparkles className={iconClass} />;
  }
};

// Build features list from tier data
const buildFeaturesList = (tier: PublicTier): string[] => {
  const features: string[] = [];

  features.push(`${tier.limits.clipboard_slots} clipboard items`);
  features.push(`${tier.limits.conversations_limit} conversations`);
  features.push(`${tier.limits.ai_requests_limit} AI requests/month`);

  if (tier.max_subusers && tier.max_subusers > 0) {
    features.push(`Up to ${tier.max_subusers} team members`);
  }

  return features;
};

export default function PublicPricing() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [plans, setPlans] = useState<PublicPlansResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getPublicPlans();
        setPlans(data);
      } catch (err: any) {
        console.error('Failed to fetch plans:', err);
        setError('Unable to load pricing plans. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Determine if a plan should be marked as popular
  const isPopularTier = (tier: PublicTier, plans: PublicTier[]): boolean => {
    // Middle tier in each category is popular
    const sortedPlans = [...plans].sort((a, b) =>
      (a.price_monthly || 0) - (b.price_monthly || 0)
    );
    const middleIndex = Math.floor(sortedPlans.length / 2);
    return sortedPlans[middleIndex]?.tier_code === tier.tier_code;
  };

  const PlanCard = ({ tier, allPlans }: { tier: PublicTier; allPlans: PublicTier[] }) => {
    const price = billingPeriod === 'monthly' ? tier.price_monthly : tier.price_yearly;
    const isPopular = isPopularTier(tier, allPlans);
    const features = buildFeaturesList(tier);
    const config = getChargeKeepConfig(tier.tier_code, billingPeriod);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

    // Handle checkout button click
    const handleCheckout = async () => {
      if (!config) return;

      setIsCheckoutLoading(true);
      try {
        await openChargeKeepPopup(config);
        // Popup opened successfully
      } catch (err) {
        console.error('ChargeKeep checkout failed:', err);
        // Fallback: open in new tab
        window.open(config.url, '_blank', 'noopener,noreferrer');
      } finally {
        setIsCheckoutLoading(false);
      }
    };

    return (
      <div className={`relative bg-dark-card/95 backdrop-blur-xl border rounded-xl p-6 flex flex-col transition-all duration-300
        ${isPopular
          ? 'border-accent-orange shadow-lg shadow-accent-orange/20 scale-105'
          : 'border-dark-border hover:border-dark-border/80'
        }`}
      >
        {/* Popular Badge */}
        {isPopular && (
          <div className="absolute top-0 right-0 bg-gradient-to-r from-accent-blue to-accent-orange text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-xs font-semibold">
            MOST POPULAR
          </div>
        )}

        {/* Plan Icon and Name */}
        <div className="flex items-center gap-3 mb-3">
          {getTierIcon(tier.tier_code, isPopular)}
          <h3 className="text-lg font-semibold text-dark-text">{tier.display_name}</h3>
        </div>

        {/* Description */}
        <p className="text-dark-text-muted text-sm mb-4">{tier.description}</p>

        {/* Price */}
        <div className="mb-4">
          {price !== null ? (
            <>
              <span className="text-3xl font-bold text-dark-text">${price.toFixed(2)}</span>
              <span className="text-dark-text-muted">/{billingPeriod === 'monthly' ? 'month' : 'year'}</span>
            </>
          ) : (
            <span className="text-2xl font-bold text-dark-text">Contact Sales</span>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-dark-text-muted">
              <Check className="w-4 h-4 text-accent-blue flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA Button - triggers ChargeKeep popup checkout */}
        <button
          onClick={handleCheckout}
          disabled={!config || isCheckoutLoading}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-base
            transition-all duration-200 transform
            focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 focus:ring-offset-dark-card
            disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
            ${isPopular
              ? 'bg-gradient-to-r from-accent-blue to-accent-orange text-white hover:opacity-90 hover:scale-105 shadow-lg'
              : 'bg-accent-blue text-white hover:bg-accent-blue/90 hover:scale-105'
            }`}
        >
          {isCheckoutLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </span>
          ) : config ? (
            `Get ${tier.display_name}`
          ) : (
            'Coming Soon'
          )}
        </button>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg text-dark-text flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-accent-blue animate-spin" />
          <p className="text-dark-text-muted">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !plans) {
    return (
      <div className="min-h-screen bg-dark-bg text-dark-text flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-dark-text">{error || 'Failed to load pricing plans'}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-dark-text">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-dark-bg/80 backdrop-blur-xl border-b border-dark-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/login')}>
            <img src={logo} alt="PARACLETE" className="h-8 w-auto" />
            <span className="text-xl font-bold bg-gradient-to-r from-accent-blue to-accent-orange bg-clip-text text-transparent">
              PARACLETE
            </span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 text-sm font-medium text-dark-text-muted hover:text-dark-text transition-colors"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-accent-blue to-accent-orange bg-clip-text text-transparent">
              Choose Your Plan
            </span>
          </h1>
          <p className="text-dark-text-muted text-lg max-w-2xl mx-auto">
            Select the subscription that best fits your research needs. All plans include 7-day grace period protection.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-12">
          <div className="bg-dark-card border border-dark-border rounded-lg p-1 inline-flex">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-md font-medium text-sm transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-inset
                ${billingPeriod === 'monthly'
                  ? 'bg-accent-blue text-white shadow-sm'
                  : 'text-dark-text-muted hover:text-dark-text'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-md font-medium text-sm transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-inset
                ${billingPeriod === 'yearly'
                  ? 'bg-accent-blue text-white shadow-sm'
                  : 'text-dark-text-muted hover:text-dark-text'
                }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Individual Plans */}
        {plans.individual_plans.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8 text-dark-text">Individual Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.individual_plans.map((tier) => (
                <PlanCard key={tier.tier_code} tier={tier} allPlans={plans.individual_plans} />
              ))}
            </div>
          </section>
        )}

        {/* Organization Plans */}
        {plans.organization_plans.length > 0 && (
          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2 text-dark-text">Organization Plans</h2>
              <p className="text-dark-text-muted">Perfect for churches, ministries, and teams</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.organization_plans.map((tier) => (
                <PlanCard key={tier.tier_code} tier={tier} allPlans={plans.organization_plans} />
              ))}
            </div>
          </section>
        )}

        {/* Why Choose Section */}
        <aside className="mt-8 max-w-3xl mx-auto bg-dark-card/50 border border-dark-border rounded-xl p-8 backdrop-blur-sm">
          <h2 className="text-xl font-bold mb-6 text-center text-dark-text">Why Choose PARACLETE?</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-dark-text">Grace Period Protection</h3>
              <p className="text-dark-text-muted text-sm">If a payment fails, you get 7 days of read-only access to export your data and update your payment method.</p>
            </div>
            <div>
              <h3 className="font-semibold text-dark-text">Flexible Billing</h3>
              <p className="text-dark-text-muted text-sm">Choose monthly or yearly billing. Yearly subscriptions save you more!</p>
            </div>
            <div>
              <h3 className="font-semibold text-dark-text">Built for Christian Research</h3>
              <p className="text-dark-text-muted text-sm">Specialized AI models trained on theological content, doctrinal presets, and tools designed for ministry work.</p>
            </div>
          </div>
        </aside>
      </main>

      {/* Footer */}
      <footer className="border-t border-dark-border py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-dark-text-muted text-sm">
          <p>&copy; {new Date().getFullYear()} PARACLETE. All rights reserved.</p>
          <p className="mt-2">
            AI-Powered Clipboard Bridge for Christian Research
          </p>
        </div>
      </footer>
    </div>
  );
}
